import i18n from '../i18n/config'

/**
 * 时间相关工具函数
 */

/**
 * 格式化时间戳为可读字符串
 */
export function formatTime(timestamp: number, locale: string = i18n.language): string {
  const date = new Date(timestamp)
  return date.toLocaleString(locale)
}

/**
 * 格式化时间戳为日期字符串
 */
export function formatDate(timestamp: number, locale: string = i18n.language): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString(locale)
}

/**
 * 格式化时间戳为时间字符串
 */
export function formatTimeOnly(timestamp: number, locale: string = i18n.language): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString(locale)
}

/**
 * 获取相对时间描述
 */
export function getRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp
  
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)
  
  if (seconds < 10) return i18n.t('common.time.justNow')
  if (seconds < 60) return i18n.t('common.time.secondsAgo', { count: seconds })
  if (minutes < 60) return i18n.t('common.time.minutesAgo', { count: minutes })
  if (hours < 24) return i18n.t('common.time.hoursAgo', { count: hours })
  if (days < 7) return i18n.t('common.time.daysAgo', { count: days })
  if (weeks < 4) return i18n.t('common.time.weeksAgo', { count: weeks })
  if (months < 12) return i18n.t('common.time.monthsAgo', { count: months })
  return i18n.t('common.time.yearsAgo', { count: years })
}

/**
 * 获取今天开始时间戳
 */
export function getTodayStart(): number {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return today.getTime()
}

/**
 * 获取昨天开始时间戳
 */
export function getYesterdayStart(): number {
  const today = getTodayStart()
  return today - 24 * 60 * 60 * 1000
}

/**
 * 获取本周开始时间戳（周一）
 */
export function getWeekStart(): number {
  const now = new Date()
  const day = now.getDay()
  const diff = now.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(now.setDate(diff))
  monday.setHours(0, 0, 0, 0)
  return monday.getTime()
}

/**
 * 获取本月开始时间戳
 */
export function getMonthStart(): number {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  return firstDay.getTime()
}

/**
 * 获取格式化的文件名（包含时间戳）
 */
export function getTimestampedFilename(baseName: string, extension: string): string {
  const now = new Date()
  const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5)
  return `${baseName}-${timestamp}.${extension}`
}

/**
 * 生成默认文档名称（紧凑格式）
 * 格式: Markflow 20250105143025367
 */
export function generateDefaultDocumentName(): string {
  const now = new Date()

  // 格式化日期时间为紧凑格式 YYYYMMDDHHmmssSSS
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0')

  const timestamp = `${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`

  return `Markflow ${timestamp}`
}

/**
 * 计算两个时间戳之间的差异
 */
export function getTimeDiff(start: number, end: number): {
  days: number
  hours: number
  minutes: number
  seconds: number
  milliseconds: number
} {
  const diff = Math.abs(end - start)
  
  const days = Math.floor(diff / (24 * 60 * 60 * 1000))
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000))
  const seconds = Math.floor((diff % (60 * 1000)) / 1000)
  const milliseconds = diff % 1000
  
  return { days, hours, minutes, seconds, milliseconds }
}

/**
 * 防抖函数（用于自动保存等场景）
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

/**
 * 节流函数（用于高频事件处理）
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let lastTime = 0
  
  return (...args: Parameters<T>) => {
    const now = Date.now()
    
    if (now - lastTime >= wait) {
      lastTime = now
      func(...args)
    }
  }
}